import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import { registerLocale } from "react-datepicker";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { toast } from "react-toastify";
import UploadFileBK from "components/Common/UploadFileBK";
import { postComment, putComment } from "store/comment/thunk";

registerLocale("en", en);
registerLocale("ko", ko);

export interface Props {
  id?: any;
  postId: any;
  parentComment?: { id: number; comment: string };
  defaultValues?: {
    id?: number;
    parentCommentId?: number | null;
    nickname?: string;
    comment?: string;
    files?: any[];
  };
  setLinkImage?: any;
  triggerRefresh?: () => Promise<void> | void;
  onCloseCommentForm?: any;
}

const CommentPostForm = ({
  id,
  postId = null,
  parentComment,
  defaultValues,
  setLinkImage,
  triggerRefresh,
  onCloseCommentForm,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const initialValues = useMemo(() => {
    const parentId = parentComment?.id ?? defaultValues?.parentCommentId ?? null;
    const parentContent = parentComment?.comment ?? "";

    return {
      parentCommentId: parentId,
      parentCommentContent: parentContent,
      nickname: defaultValues?.nickname || "",
      comment: defaultValues?.comment || "",
      files: defaultValues?.files ? [...defaultValues.files] : [],
    };
  }, [parentComment, defaultValues]);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      let data: any = {
        source: "real",
        type: "system",
        approvalStatus: "APPROVED",
        postId: postId,
        parentCommentId: values?.parentCommentId || null,
        nickname: values?.nickname,
        comment: values?.comment,
        files: values?.files,
      };

      const response: any = id
        ? await putComment(id, data)
        : await postComment(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${
            id
              ? t("The comment has been updated.")
              : t("The comment has been created.")
          }`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        if (triggerRefresh) {
          await triggerRefresh();
        }
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    nickname: Yup.string().required(`${t("This field is required")}`),
    comment: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    document.title = `${t("Create Comment")} - ${t("Community")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    setIsFirstProgress(true);
  }, [initialValues]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="l">
        <ModalHeader
          toggle={
            isLoading ? () => {} : onCloseCommentForm && onCloseCommentForm
          }
        >
          {id ? t("Update Content") : t("Create Content")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 2) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <form onSubmit={formik.handleSubmit}>
              <Row>
                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="nickname" className="form-label">
                      {t("Nick Name")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="nickname"
                      name="nickname"
                      type="text"
                      className="form-control"
                      placeholder={t("Nick Name")}
                      value={formik?.values?.nickname}
                      onChange={(event: any) =>
                        formik.setFieldValue("nickname", event?.target?.value)
                      }
                    />
                    {formik.touched.nickname && formik.errors.nickname ? (
                      <div className="text-danger mt-2">
                        {String(formik.errors.nickname)}
                      </div>
                    ) : null}
                  </div>
                </Col>

                {!!formik.values.parentCommentId && (
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="parentCommentContent" className="form-label">
                        {t("Parent Comment")}
                      </Label>
                      <Input
                        id="parentCommentContent"
                        name="parentCommentContent"
                        type="textarea"
                        rows="3"
                        className="form-control"
                        value={formik?.values?.parentCommentContent || ""}
                        disabled
                      />
                    </div>
                  </Col>
                )}

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="comment" className="form-label">
                      {t("Content")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="comment"
                      name="comment"
                      type="textarea"
                      rows="5"
                      maxLength={1000}
                      className="form-control"
                      placeholder={t("Content")}
                      value={formik?.values?.comment}
                      onChange={(event: any) =>
                        formik.setFieldValue("comment", event?.target?.value)
                      }
                    />
                    {formik.touched.comment && formik.errors.comment ? (
                      <div className="text-danger mt-2">
                        {String(formik.errors.comment)}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="store detail photo" className="form-label">
                      {t("Files")}
                      <br />
                      <small>
                        {t(
                          "Upload up to 100MB in jpg, png image file format. Maximize 5 file"
                        )}
                      </small>
                    </Label>
                    <UploadFileBK
                      formik={formik}
                      fieldValue="files"
                      isMultiple={true}
                      isUploadMore={
                        (formik?.values?.files &&
                        (formik?.values?.files as any)?.length >= 5
                          ? false
                          : true) || !formik?.values?.files
                      }
                      setLinkImage={setLinkImage}
                    />
                    {formik.touched.files && formik.errors.files ? (
                      <div className="text-danger mt-2">
                        {String(formik.errors.files)}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end">
                    <button type="submit" className="btn btn-primary fs-14">
                      {isLoading ? (
                        <Spinner size="sm" className="me-2"></Spinner>
                      ) : !!id ? (
                        <i className="ri-login-circle-line align-bottom me-2 "></i>
                      ) : (
                        <i className="ri-add-fill align-bottom me-2"></i>
                      )}
                      {id ? t("Update Comment") : t("Create Comment")}
                    </button>
                  </div>
                </Col>
              </Row>
            </form>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CommentPostForm;
